import { useEffect, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { withTranslation } from "react-i18next";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import debug from "@plannel/utils/debug-util";

const FP_MODULE = "FP";

/**
 * FP 계획범위(Plan Scope) 선택 Autocomplete — 모든 FP 화면 공용.
 * 전체 계획범위가 아니라 **해당 모듈(FP)에 매핑되고 use_yn='Y'인 범위만** 로드
 * (`GET /common/planscope/modules/module?module-cd=FP`, plan_scope 조인으로 planScopeNm 포함,
 * display_seq 정렬). 예: DOMESTIC은 MP 전용이라 FP 목록엔 안 뜸.
 * - controlled: 부모가 value + onChange 보유.
 * - 값이 비었거나 목록에 없으면 **맨 위 항목(display_seq 최상단)**을 자동 선택해 onChange로 통지
 *   → 부모가 그 값으로 최초 조회. (사용자 지시: "최초에 맨상단의 PlanScope이 선택되고 그걸로 조회")
 * - 레거시 규칙: 범위가 1개뿐이면 셀렉터를 렌더하지 않음(숨김).
 * (example/시나리오 모드는 이번 범위에서 제외 — 시뮬레이션 붙일 때 별도.)
 *
 * props: value, onChange(planScope), label?, module?(기본 "FP")
 */
const PlanScope = ({ t, value, onChange, label, module = FP_MODULE }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    fpCommonService.getModulePlanScopes(module)
      .then(response => {
        const list = (response.data ?? [])
          .filter(d => d.useYn)                        // use_yn = 'Y'만
          .map(d => ({ value: d.planScope, label: d.planScopeNm || d.planScope }));
        setOptions(list);

        // value가 비었거나 목록에 없으면 맨 위(displaySeq 최상단) 항목을 선택 → 부모가 그 값으로 조회.
        if (list.length > 0 && !list.some(o => o.value === value)) {
          const top = list[0];
          if (top.value !== value) onChange?.(top.value);
        }
      })
      .catch(e => debug(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  // 범위가 1개 이하면 선택 UI를 숨김(레거시 동일). 값은 부모가 유지하므로 조회엔 영향 없음.
  if (options.length <= 1) return null;

  const labelText = label ?? t("FP_PLAN_SCOPE");
  // 아직 목록에 없는 값이면 null(곧 자동선택 useEffect로 보정됨) — 외부 계약(value=코드 문자열)은
  // 그대로 유지하고, Autocomplete가 요구하는 옵션 객체로 내부에서만 변환.
  const selectedOption = options.find(o => o.value === value) ?? null;

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 190 }}
      options={options}
      getOptionLabel={option => option?.label ?? ""}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      value={selectedOption}
      onChange={(_, option) => onChange?.(option?.value ?? null)}
      disableClearable
      renderInput={(params) => <TextField {...params} label={labelText} />}
    />
  );
};

export default withTranslation()(PlanScope);
